import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import DynamicTitle from '@/components/DynamicTitle';

export default function VibeSignup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({
        title: "Սխալ",
        description: "Խնդրում եմ մուտքագրի անունդ",
        variant: "destructive",
      });
      return;
    }

    if (!email) {
      toast({
        title: "Սխալ",
        description: "Խնդրում եմ մուտքագրի քո էլ. փոստի հասցեն",
        variant: "destructive",
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Սխալ",
        description: "Խնդրում եմ մուտքագրի վավերական էլ. փոստի հասցե",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmParam = urlParams.toString();

      await fetch('https://fully.tezzarida.io/webhook/vibe-coding-course', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email,
          timestamp: new Date().toISOString(),
          source: 'vibe_signup_page',
          utm_params: utmParam,
          url: `${window.location.origin}/vibe-signup${utmParam ? '?' + utmParam : ''}`,
        }),
      });

      toast({
        title: "Թույն!",
        description: "Մի քանի վայրկյանից մուտքի տվյալները կուղարկվեն մեյլիդ։",
      });

      setIsSuccess(true);
      setName('');
      setEmail('');
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ուղարկել քո էլ. փոստը: Խնդրում եմ կրկին փորձել:",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <DynamicTitle title="Vibe Coding Course — Մուտքի տվյալներ ստանալու համար" />
      <Header />

      <main className="container mx-auto px-4 py-20">
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center">
          <div className="max-w-4xl mx-auto space-y-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white glow-text leading-tight">
              Գրի մեյլդ, որ ուղարկեմ քո մուտքի տվյալները «Վայբ Քոդինգ» կուրսին
            </h1>

            <div className="max-w-md mx-auto space-y-6">
              {isSuccess ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border-2 border-gray-300 bg-white px-6 py-8 text-black">
                    <h2 className="text-xl font-semibold mb-2">Պատրաստ ա ✓</h2>
                    <p className="text-gray-700">
                      Մի քանի վայրկյանից կստանաս քո մուտքի տվյալները մեյլիդ։ Ստուգի նաև «Spam» թղթապանակը։
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setIsSuccess(false)}
                    className="w-full h-12 text-lg font-semibold bg-black hover:bg-black text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-[0_0_20px_rgba(124,58,237,0.5)] hover:shadow-[0_0_25px_rgba(124,58,237,0.8)]"
                  >
                    Ուրիշ մեյլով ուղարկել
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Անունդ ոնց ա"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full h-12 text-lg bg-white border-2 border-gray-300 rounded-full px-6 text-black placeholder:text-gray-500 focus:border-gray-400 focus:ring-0 focus:outline-none transition-colors duration-300"
                      disabled={isLoading}
                      autoComplete="given-name"
                    />
                  </div>

                  <div className="relative">
                    <Input
                      type="email"
                      placeholder="Մուտքագրի քո էլ. փոստի հասցեն"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-12 text-lg bg-white border-2 border-gray-300 rounded-full px-6 text-black placeholder:text-gray-500 focus:border-gray-400 focus:ring-0 focus:outline-none transition-colors duration-300"
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || !name || !email}
                    className="w-full h-12 text-lg font-semibold bg-black hover:bg-black text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-[0_0_20px_rgba(124,58,237,0.5)] hover:shadow-[0_0_25px_rgba(124,58,237,0.8)] disabled:opacity-50 disabled:hover:scale-100 disabled:shadow-[0_0_20px_rgba(124,58,237,0.5)]"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                        Ուղարկվում է...
                      </div>
                    ) : (
                      'Ստանալ մուտքի տվյալները'
                    )}
                  </Button>
                </form>
              )}

              <p className="text-gray-400 text-sm">
                կնոպկեն սղմելուց հետո միանգամից կարաս ստուգես մեյլդ
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
